import { useQuery } from '@tanstack/react-query'
import { createContext, useContext } from 'react'
import { api } from '@/lib/api/client'
import { useLang } from '@/lib/i18n'
import { ADDRESS_PATH } from './boot'
import { tenantOf } from './model'
import type { AddressBoot, AddressProfile, TenantAddress } from './types'

/** What the boot learnt about this address (P12), for every screen below the gate. */
export const AddressContext = createContext<AddressBoot | null>(null)

/** The boot's answer as it came: the kind of address, or none, or offline. */
export function useAddressBoot(): AddressBoot | null {
  return useContext(AddressContext)
}

/**
 * The company whose address this is — its name, logos, icons, brand kit,
 * attribution, languages and canonical origins — or null at the platform's
 * address, at nobody's, or when the API could not be asked. The name is in
 * the reader's language: after a language switch the address is asked again.
 */
export function useAddress(): TenantAddress | null {
  const boot = useContext(AddressContext)
  const { lang } = useLang()
  const seeded = tenantOf(boot)
  const again = seeded !== null && boot?.state === 'known' && boot.lang !== lang

  const query = useQuery({
    queryKey: ['address', lang] as const,
    queryFn: async ({ signal }) => (await api.get<{ data: AddressProfile }>(ADDRESS_PATH, { headers: { 'Accept-Language': lang }, signal })).data,
    enabled: again,
    staleTime: 5 * 60_000,
    retry: false,
  })

  const fresh = query.data?.kind === 'tenant' ? query.data : null

  return again ? (fresh ?? seeded) : seeded
}

/**
 * Whose name a page title ends with: the product's, or — when the company
 * hides the platform's line on a white-label plan — the company's own.
 */
export function useProductTitle(): string {
  const address = useAddress()
  return address !== null && address.attribution === false ? address.company.name : 'Umrah Link'
}
